import { addDays, format, parseISO } from "date-fns"
import { DistributorSalesReport, DistributorSource } from "../types/distributor_sales"

/** Distributors with their own tab on Sales Analysis. */
export type DistributorSalesTabSource = Extract<DistributorSource, "kehe" | "mclane">

const SOURCE_LABELS: Record<DistributorSource, string> = {
  kehe: "KeHE",
  unfi: "UNFI",
  spins: "Syndicated Data",
  mclane: "McLane",
}

export function distributor_source_label(source: DistributorSource): string {
  return SOURCE_LABELS[source] ?? source.toUpperCase()
}

function by_recency(a: DistributorSalesReport, b: DistributorSalesReport): number {
  if (a.report_date !== b.report_date) return b.report_date.localeCompare(a.report_date)
  return b.created_at.localeCompare(a.created_at)
}

/**
 * A McLane upload is split into one report per week plus one summary over the whole file. The
 * summary is the useful default for the table; weeks feed the trend chart.
 */
export function latest_shipments_summary(reports: DistributorSalesReport[]): DistributorSalesReport | null {
  const summaries = reports.filter((r: DistributorSalesReport) => r.report_type === "shipments_summary")
  return summaries.length > 0 ? [...summaries].sort(by_recency)[0] : null
}

/** Weeks the distributor sync pulled, read as one report (see `server/src/distributor_sales_synced.rs`). */
export function is_synced_report(report: DistributorSalesReport | null | undefined): boolean {
  return report?.report_type === "synced_weeks"
}

/** The id the server reads a span of synced weeks by: `synced:kehe:2026-01-01:2026-10-24`. */
export function synced_report_id(source: DistributorSource, start_date: string, end_date: string): string {
  return `synced:${source}:${start_date}:${end_date}`
}

export interface SyncedWeek {
  start_date: string
  end_date: string
}

/** Sunday-to-Saturday weeks from `start_date` to `end_date`, the first clipped to its start, as the sync pulls them. */
export function synced_weeks(start_date: string, end_date: string): SyncedWeek[] {
  const weeks: SyncedWeek[] = []
  const last = parseISO(end_date)
  let start = parseISO(start_date)
  while (start <= last) {
    const saturday = addDays(start, 6 - start.getDay())
    const end = saturday > last ? last : saturday
    weeks.push({ start_date: format(start, "yyyy-MM-dd"), end_date: format(end, "yyyy-MM-dd") })
    start = addDays(saturday, 1)
  }
  return weeks
}

/** The synced report narrowed to a span of its weeks. */
export function synced_report_for_range(report: DistributorSalesReport, start_date: string, end_date: string): DistributorSalesReport {
  return { ...report, id: synced_report_id(report.source, start_date, end_date), start_date, end_date, report_date: end_date }
}

/** Selector order: synced weeks first (they supersede uploads), then whole-file summaries, then everything else newest first. */
export function order_reports_for_selector(reports: DistributorSalesReport[]): DistributorSalesReport[] {
  const rank = (r: DistributorSalesReport) => (is_synced_report(r) ? 0 : r.report_type === "shipments_summary" ? 1 : 2)
  return [...reports].sort((a: DistributorSalesReport, b: DistributorSalesReport) => {
    if (rank(a) !== rank(b)) return rank(a) - rank(b)
    return by_recency(a, b)
  })
}

export function report_option_label(report: DistributorSalesReport): string {
  const label = distributor_source_label(report.source)
  if (is_synced_report(report) && report.start_date && report.end_date) {
    return `${label} · Synced weekly (${format(parseISO(report.start_date), "MMM d, yyyy")} – ${format(parseISO(report.end_date), "MMM d, yyyy")})`
  }
  if (report.report_type === "shipments_summary" && report.start_date && report.end_date) {
    return `${label} · All weeks (${format(parseISO(report.start_date), "MMM d, yyyy")} – ${format(parseISO(report.end_date), "MMM d, yyyy")})`
  }
  if (report.report_type === "weekly_shipments" && report.end_date) {
    return `${label} · Week ending ${format(parseISO(report.end_date), "MMM d, yyyy")}`
  }
  const date_str = format(parseISO(report.report_date), "MMM d, yyyy")
  const range_str =
    report.start_date && report.end_date
      ? ` (${format(parseISO(report.start_date), "MMM d")} - ${format(parseISO(report.end_date), "MMM d")})`
      : ""
  return `${label} · ${date_str}${range_str}`
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  function report(overrides: Partial<DistributorSalesReport>): DistributorSalesReport {
    return {
      id: "r",
      org_id: "o",
      source: "mclane",
      report_type: "weekly_shipments",
      report_date: "2026-07-31",
      start_date: "2026-07-25",
      end_date: "2026-07-31",
      file_name: "mclane.csv",
      created_at: "2026-09-14T00:00:00Z",
      updated_at: "2026-09-14T00:00:00Z",
      ...overrides,
    } as DistributorSalesReport
  }

  describe("latest_shipments_summary", () => {
    it("picks the newest summary and ignores weekly reports", () => {
      const reports = [
        report({ id: "week" }),
        report({ id: "old", report_type: "shipments_summary", report_date: "2026-06-26" }),
        report({ id: "new", report_type: "shipments_summary", report_date: "2026-07-31" }),
      ]
      expect(latest_shipments_summary(reports)?.id).toBe("new")
      expect(latest_shipments_summary([report({})])).toBeNull()
    })
  })

  describe("order_reports_for_selector", () => {
    it("lists summaries before weeks, each newest first", () => {
      const reports = [
        report({ id: "w1", report_date: "2026-07-24" }),
        report({ id: "s", report_type: "shipments_summary", report_date: "2026-07-31" }),
        report({ id: "w2", report_date: "2026-07-31" }),
      ]
      expect(order_reports_for_selector(reports).map((r: DistributorSalesReport) => r.id)).toEqual(["s", "w2", "w1"])
    })
  })

  describe("synced weeks", () => {
    it("lists Sunday-to-Saturday weeks, the first clipped to the start", () => {
      expect(synced_weeks("2026-01-01", "2026-01-17")).toEqual([
        { start_date: "2026-01-01", end_date: "2026-01-03" },
        { start_date: "2026-01-04", end_date: "2026-01-10" },
        { start_date: "2026-01-11", end_date: "2026-01-17" },
      ])
      expect(synced_weeks("2026-10-18", "2026-10-24")).toEqual([{ start_date: "2026-10-18", end_date: "2026-10-24" }])
    })

    it("addresses a span of weeks by its range", () => {
      const synced = report({ id: "synced:kehe:2026-01-01:2026-10-24", source: "kehe", report_type: "synced_weeks", start_date: "2026-01-01", end_date: "2026-10-24" })
      const narrowed = synced_report_for_range(synced, "2026-09-27", "2026-10-24")
      expect(narrowed.id).toBe("synced:kehe:2026-09-27:2026-10-24")
      expect([narrowed.start_date, narrowed.end_date, narrowed.report_date]).toEqual(["2026-09-27", "2026-10-24", "2026-10-24"])
      expect(is_synced_report(narrowed)).toBe(true)
      expect(is_synced_report(report({}))).toBe(false)
    })

    it("lists synced weeks before anything uploaded", () => {
      const reports = [
        report({ id: "s", report_type: "shipments_summary", report_date: "2026-07-31" }),
        report({ id: "synced", report_type: "synced_weeks", report_date: "2026-01-10" }),
      ]
      expect(order_reports_for_selector(reports).map((r: DistributorSalesReport) => r.id)).toEqual(["synced", "s"])
    })
  })

  describe("report_option_label", () => {
    it("describes McLane summaries and weeks", () => {
      expect(
        report_option_label(report({ report_type: "shipments_summary", start_date: "2024-12-28", end_date: "2026-07-31" }))
      ).toBe("McLane · All weeks (Dec 28, 2024 – Jul 31, 2026)")
      expect(report_option_label(report({}))).toBe("McLane · Week ending Jul 31, 2026")
    })

    it("names synced weeks by their span", () => {
      expect(
        report_option_label(report({ source: "kehe", report_type: "synced_weeks", start_date: "2026-01-01", end_date: "2026-10-24" }))
      ).toBe("KeHE · Synced weekly (Jan 1, 2026 – Oct 24, 2026)")
    })

    it("keeps the KeHE format", () => {
      expect(
        report_option_label(
          report({ source: "kehe", report_type: "vendor_sales", report_date: "2026-01-10", start_date: "2026-01-04", end_date: "2026-01-10" })
        )
      ).toBe("KeHE · Jan 10, 2026 (Jan 4 - Jan 10)")
    })
  })
}
